import React, { useState, useRef, useEffect, useCallback } from "react";
import "./NodeEditor.scss";

const LIBRARY_ITEMS = [
  { 
    id: "item1", 
    label: "Wave", 
    icon: "🌊",
    dummyData: {
      duration: "5s",
      amplitude: "High",
      frequency: "2Hz"
    }
  },
  { 
    id: "item2", 
    label: "Quiz", 
    icon: "❓",
    dummyData: {
      question: "What is your favorite color?",
      options: ["Red", "Blue", "Green"],
      timer: "30s"
    }
  },
  { 
    id: "item3", 
    label: "Video", 
    icon: "🎬",
    dummyData: {
      file: "intro_video.mp4",
      duration: "15s",
      autoplay: true
    }
  },
  { 
    id: "item4", 
    label: "Audio", 
    icon: "🔊",
    dummyData: {
      file: "background_music.mp3",
      volume: "70%",
      loop: true,
      fadeIn: "2s"
    }
  },
];

const GRID_SIZE = 20;
const CONTAINER_WIDTH = 2000;
const CONTAINER_HEIGHT = 2000;

function TimelineEditor() {
  const [flowItems, setFlowItems] = useState([
    {
      id: "plus-center",
      type: "plus",
      x: CONTAINER_WIDTH / 2 - 16,
      y: CONTAINER_HEIGHT / 2 - 16,
      anchors: ["left", "right"],
    },
  ]);

  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedPanel, setDraggedPanel] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);
  const [panOffset, setPanOffset] = useState({ x: -CONTAINER_WIDTH / 4, y: -CONTAINER_HEIGHT / 4 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  
  const flowAreaRef = useRef(null);

  // Generate grid dots based on container size
  const generateGridDots = useCallback(() => {
    const dots = [];
    const dotsX = Math.floor(CONTAINER_WIDTH / GRID_SIZE);
    const dotsY = Math.floor(CONTAINER_HEIGHT / GRID_SIZE);
    
    for (let i = 0; i <= dotsX; i++) {
      for (let j = 0; j <= dotsY; j++) {
        dots.push({
          key: `${i}-${j}`,
          x: i * GRID_SIZE,
          y: j * GRID_SIZE,
        });
      }
    }
    return dots;
  }, []);

  // Generate connection lines between panels and plus signs
  const generateConnections = useCallback(() => {
    const connections = [];
    const panels = flowItems.filter(item => item.type === 'panel').sort((a, b) => a.x - b.x);
    const plusSigns = flowItems.filter(item => item.type === 'plus').sort((a, b) => a.x - b.x);
    
    // Connect panels to each other with stepped lines
    for (let i = 0; i < panels.length - 1; i++) {
      const start = panels[i];
      const end = panels[i + 1];
      
      const startY = start.y + start.height / 2;
      const endY = end.y + end.height / 2;
      const midX = start.x + start.width + (end.x - start.x - start.width) / 2;
      
      // Horizontal line from start panel
      connections.push({
        key: `connection-panel-h1-${start.id}-${end.id}`,
        x1: start.x + start.width,
        y1: startY,
        x2: midX,
        y2: startY,
      });
      
      // Vertical line in the middle (if heights are different)
      if (startY !== endY) {
        connections.push({
          key: `connection-panel-v-${start.id}-${end.id}`,
          x1: midX,
          y1: startY,
          x2: midX,
          y2: endY,
        });
      }
      
      // Horizontal line to end panel
      connections.push({
        key: `connection-panel-h2-${start.id}-${end.id}`,
        x1: midX,
        y1: endY,
        x2: end.x,
        y2: endY,
      });
    }
    
    // Connect left plus sign to first panel with straight line
    if (plusSigns.length > 0 && panels.length > 0) {
      const leftPlus = plusSigns[0];
      const firstPanel = panels[0];
      
      if (leftPlus.x < firstPanel.x) {
        const plusY = leftPlus.y + 16; // Plus sign center
        const panelY = firstPanel.y + firstPanel.height / 2;
        
        // Straight line from plus sign to panel
        connections.push({
          key: `connection-plus-left-${leftPlus.id}-${firstPanel.id}`,
          x1: leftPlus.x + 32,
          y1: plusY,
          x2: firstPanel.x,
          y2: panelY,
        });
      }
      
      // Connect last panel to right plus sign with straight line
      const rightPlus = plusSigns[plusSigns.length - 1];
      const lastPanel = panels[panels.length - 1];
      
      if (rightPlus.x > lastPanel.x) {
        const panelY = lastPanel.y + lastPanel.height / 2;
        const plusY = rightPlus.y + 16; // Plus sign center
        
        // Straight line from panel to plus sign
        connections.push({
          key: `connection-panel-plus-${lastPanel.id}-${rightPlus.id}`,
          x1: lastPanel.x + lastPanel.width,
          y1: panelY,
          x2: rightPlus.x,
          y2: plusY,
        });
      }
    }
    
    return connections;
  }, [flowItems]);

  const connections = generateConnections();
  const gridDots = generateGridDots();

  // Pan functionality
  const handleMouseDown = (e) => {
    if (e.target.classList.contains('flow-container') || e.target.classList.contains('grid-bg')) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newZoom = Math.min(Math.max(0.5, zoom + delta), 2);
    setZoom(newZoom);
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, lastPanPoint]);

  // Handle drop on plus sign
  const handleDrop = (e, plusItem) => {
    e.preventDefault();
    if (!draggedItem && !draggedPanel) return;

    // Snap to grid
    const snap = (val) => Math.round(val / GRID_SIZE) * GRID_SIZE;

    // Check if this is an insertion between panels
    const insertIndex = plusItem.insertIndex;

    // Handle dropping an existing panel (reordering)
    if (draggedPanel) {
      setFlowItems((items) => {
        // Get all panels except the dragged one
        let allPanels = items.filter(item => item.type === 'panel' && item.id !== draggedPanel.id).sort((a, b) => a.x - b.x);
        
        // Insert the dragged panel at the correct position
        if (insertIndex !== undefined) {
          allPanels.splice(insertIndex, 0, draggedPanel);
        } else {
          // If no specific insert index, add to the end
          allPanels.push(draggedPanel);
        }
        
        // Redistribute panels with proper spacing
        const PANEL_SPACING = 280; // Increased spacing between panel centers
        const redistributedPanels = allPanels.map((panel, index) => ({
          ...panel,
          x: snap(CONTAINER_WIDTH / 2 - (allPanels.length - 1) * PANEL_SPACING / 2 + index * PANEL_SPACING - 90)
        }));
        
        // Remove all existing plus signs and panels, then add redistributed panels
        const otherItems = items.filter(item => item.type !== 'plus' && item.type !== 'panel');
        const finalItems = [...otherItems, ...redistributedPanels];
        
        if (redistributedPanels.length > 0) {
          const leftmostPanel = redistributedPanels[0];
          const rightmostPanel = redistributedPanels[redistributedPanels.length - 1];
          
          // Add plus sign to the left of the leftmost panel
          finalItems.push({
            id: `plus-left-${Date.now()}`,
            type: "plus",
            x: snap(leftmostPanel.x - 120),
            y: snap(leftmostPanel.y + (leftmostPanel.height / 2) - 16),
            anchors: ["left"],
          });
          
          // Add plus sign to the right of the rightmost panel
          finalItems.push({
            id: `plus-right-${Date.now()}`,
            type: "plus",
            x: snap(rightmostPanel.x + rightmostPanel.width + 80),
            y: snap(rightmostPanel.y + (rightmostPanel.height / 2) - 16),
            anchors: ["right"],
          });
          
          // Add plus signs between panels for insertion
          for (let i = 0; i < redistributedPanels.length - 1; i++) {
            const leftPanel = redistributedPanels[i];
            const rightPanel = redistributedPanels[i + 1];
            const insertionX = leftPanel.x + leftPanel.width + (rightPanel.x - leftPanel.x - leftPanel.width) / 2;
            
            finalItems.push({
              id: `plus-insert-${i + 1}-${Date.now()}`,
              type: "plus",
              x: snap(insertionX - 16),
              y: snap(leftPanel.y + (leftPanel.height / 2) - 16),
              anchors: ["insert"],
              insertIndex: i + 1,
              isInsertionPoint: true,
              style: { opacity: 0 }
            });
          }
        }
        
        return finalItems;
      });
      
      setDraggedPanel(null);
      setDragOverPosition(null);
      return;
    }

    // Handle dropping a new item from library
    if (draggedItem) {
      // Determine the Y position - use existing panels' Y or center if first panel
      const existingPanels = flowItems.filter(item => item.type === 'panel');
      const baseY = existingPanels.length > 0 
        ? existingPanels[0].y 
        : CONTAINER_HEIGHT / 2 - 30;

      // Determine panel dimensions based on type - make them more like wireframe cards
      const panelHeight = draggedItem.label === 'Audio' ? 200 : 160; // Wider and a bit taller
      const panelWidth = 180; // Wider cards
      
      // Place new item at plus sign position (center the panel on the plus sign)
      const newItem = {
        id: `flow-${Date.now()}`,
        type: "panel",
        label: draggedItem.label,
        icon: draggedItem.icon,
        dummyData: draggedItem.dummyData,
        x: snap(plusItem.x - 90), // Center 180px wide panel on plus sign
        y: snap(baseY), // Align with other panels
        width: panelWidth,
        height: panelHeight,
      };

      setFlowItems((items) => {
        // Remove the used plus sign and add the new panel
        const filteredItems = items.filter((item) => item.id !== plusItem.id);
        const newItems = [...filteredItems, newItem];
        
        // Get all panels (including the new one) and sort by x position
        let allPanels = newItems.filter(item => item.type === 'panel').sort((a, b) => a.x - b.x);
        
        // If this is an insertion, we need to reorder the panels
        if (insertIndex !== undefined) {
          // Remove the new panel from its current position
          const newPanel = allPanels.find(panel => panel.id === newItem.id);
          allPanels = allPanels.filter(panel => panel.id !== newItem.id);
          
          // Insert it at the correct position
          allPanels.splice(insertIndex, 0, newPanel);
        }
        
        // Redistribute panels with proper spacing (280px between panel centers for wider panels)
        const PANEL_SPACING = 280; // Increased spacing between panel centers
        const redistributedPanels = allPanels.map((panel, index) => ({
          ...panel,
          x: snap(CONTAINER_WIDTH / 2 - (allPanels.length - 1) * PANEL_SPACING / 2 + index * PANEL_SPACING - 90)
        }));
        
        // Remove all existing plus signs and panels, then add redistributed panels
        const otherItems = newItems.filter(item => item.type !== 'plus' && item.type !== 'panel');
        const finalItems = [...otherItems, ...redistributedPanels];
        
        if (redistributedPanels.length > 0) {
          const leftmostPanel = redistributedPanels[0];
          const rightmostPanel = redistributedPanels[redistributedPanels.length - 1];
          
          // Add plus sign to the left of the leftmost panel (aligned with panels)
          finalItems.push({
            id: `plus-left-${Date.now()}`,
            type: "plus",
            x: snap(leftmostPanel.x - 120), // More space between plus and panel
            y: snap(leftmostPanel.y + (leftmostPanel.height / 2) - 16), // Center plus sign with panel center
            anchors: ["left"],
          });
          
          // Add plus sign to the right of the rightmost panel (aligned with panels)
          finalItems.push({
            id: `plus-right-${Date.now()}`,
            type: "plus",
            x: snap(rightmostPanel.x + rightmostPanel.width + 80), // More space between panel and plus
            y: snap(rightmostPanel.y + (rightmostPanel.height / 2) - 16), // Center plus sign with panel center
            anchors: ["right"],
          });
          
          // Add plus signs between panels for insertion
          for (let i = 0; i < redistributedPanels.length - 1; i++) {
            const leftPanel = redistributedPanels[i];
            const rightPanel = redistributedPanels[i + 1];
            const insertionX = leftPanel.x + leftPanel.width + (rightPanel.x - leftPanel.x - leftPanel.width) / 2;
            
            finalItems.push({
              id: `plus-insert-${i + 1}-${Date.now()}`,
              type: "plus",
              x: snap(insertionX - 16),
              y: snap(leftPanel.y + (leftPanel.height / 2) - 16),
              anchors: ["insert"],
              insertIndex: i + 1,
              isInsertionPoint: true,
              style: { opacity: 0 } // Hidden by default
            });
          }
        }
        
        return finalItems;
      });
      
      setDraggedItem(null);
      setDragOverPosition(null);
    }
  };

  // Handle drag over the flow area to detect insertion points
  const handleFlowAreaDragOver = (e) => {
    e.preventDefault();
    if (!draggedItem && !draggedPanel) return;

    const flowArea = flowAreaRef.current;
    if (!flowArea) return;

    const rect = flowArea.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / zoom;
    const y = (e.clientY - rect.top - panOffset.y) / zoom;

    // Filter out the dragged panel when checking for insertion points
    const panels = flowItems.filter(item => 
      item.type === 'panel' && 
      (!draggedPanel || item.id !== draggedPanel.id)
    ).sort((a, b) => a.x - b.x);
    
    if (panels.length < 1) {
      setDragOverPosition(null);
      return;
    }

    // If we only have one panel (or dragging the only other panel), allow insertion at start/end
    if (panels.length === 1) {
      const panel = panels[0];
      const panelCenterY = panel.y + panel.height / 2;
      const topBound = panel.y - 20;
      const bottomBound = panel.y + panel.height + 20;
      
      if (y >= topBound && y <= bottomBound) {
        if (x < panel.x) {
          // Insert before the panel
          setDragOverPosition({
            x: panel.x - 60,
            y: panelCenterY - 16,
            insertIndex: 0,
            leftPanelId: null,
            rightPanelId: panel.id
          });
          return;
        } else if (x > panel.x + panel.width) {
          // Insert after the panel
          setDragOverPosition({
            x: panel.x + panel.width + 60,
            y: panelCenterY - 16,
            insertIndex: 1,
            leftPanelId: panel.id,
            rightPanelId: null
          });
          return;
        }
      }
    }

    // Check if we're between any two panels
    for (let i = 0; i < panels.length - 1; i++) {
      const leftPanel = panels[i];
      const rightPanel = panels[i + 1];
      
      const leftBound = leftPanel.x + leftPanel.width;
      const rightBound = rightPanel.x;
      const topBound = Math.min(leftPanel.y, rightPanel.y) - 20;
      const bottomBound = Math.max(leftPanel.y + leftPanel.height, rightPanel.y + rightPanel.height) + 20;
      
      if (x >= leftBound && x <= rightBound && y >= topBound && y <= bottomBound) {
        const insertionX = leftBound + (rightBound - leftBound) / 2;
        const insertionY = leftPanel.y + leftPanel.height / 2;
        
        // Calculate the correct insert index based on the original panel positions
        let adjustedInsertIndex = i + 1;
        if (draggedPanel) {
          // If we're moving an existing panel, we need to account for its removal
          const draggedPanelOriginalIndex = flowItems.filter(item => item.type === 'panel')
            .sort((a, b) => a.x - b.x)
            .findIndex(panel => panel.id === draggedPanel.id);
          
          if (draggedPanelOriginalIndex <= i) {
            adjustedInsertIndex = i;
          }
        }
        
        setDragOverPosition({
          x: insertionX - 16,
          y: insertionY - 16,
          insertIndex: adjustedInsertIndex,
          leftPanelId: leftPanel.id,
          rightPanelId: rightPanel.id
        });
        return;
      }
    }
    
    setDragOverPosition(null);
  };

  // Handle drag leave to reset positions
  const handleFlowAreaDragLeave = (e) => {
    // Only reset if we're actually leaving the flow area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverPosition(null);
    }
  };

  // Handle drop on insertion point
  const handleInsertionDrop = (e) => {
    e.preventDefault();
    if ((!draggedItem && !draggedPanel) || !dragOverPosition) return;

    // Create a temporary plus item for the drop handler
    const tempPlusItem = {
      id: 'temp-insertion-plus',
      x: dragOverPosition.x,
      y: dragOverPosition.y,
      insertIndex: dragOverPosition.insertIndex
    };

    handleDrop(e, tempPlusItem);
  };

  // Handle panel drag start
  const handlePanelDragStart = (panel) => {
    setDraggedPanel(panel);
  };

  // Handle panel drag end
  const handlePanelDragEnd = () => {
    setDraggedPanel(null);
    setDragOverPosition(null);
  };

  return (
    <div className="timeline-editor-grid">
      {/* Left Library */}
      <div className="timeline-editor-col library">
        <h3>Library</h3>
        <div className="library-items">
          {LIBRARY_ITEMS.map((item) => (
            <div
              key={item.id}
              className="library-item"
              draggable
              onDragStart={() => setDraggedItem(item)}
              onDragEnd={() => setDraggedItem(null)}
            >
              <span style={{ marginRight: '0.5rem' }}>{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Main Flow Area */}
      <div 
        className={`timeline-editor-col flow-area ${isPanning ? 'panning' : ''}`}
        ref={flowAreaRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onDragOver={handleFlowAreaDragOver}
        onDragLeave={handleFlowAreaDragLeave}
        onDrop={handleInsertionDrop}
      >
        <div 
          className="flow-container"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            width: CONTAINER_WIDTH,
            height: CONTAINER_HEIGHT,
          }}
        >
          <div className="grid-bg">
            {/* Render grid dots */}
            {gridDots.map((dot) => (
              <div
                key={dot.key}
                className="grid-dot"
                style={{
                  left: dot.x,
                  top: dot.y,
                }}
              />
            ))}
          </div>
          
          {/* Render connection lines */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            {connections.map((connection) => (
              <g key={connection.key}>
                <line
                  x1={connection.x1}
                  y1={connection.y1}
                  x2={connection.x2}
                  y2={connection.y2}
                  stroke="#007bff"
                  strokeWidth="2"
                  strokeDasharray="4,4"
                />
                {/* Add dot at the end of each line segment */}
                <circle
                  cx={connection.x2}
                  cy={connection.y2}
                  r="3"
                  fill="#007bff"
                />
              </g>
            ))}
          </svg>
          
          {/* Render flow items above the grid */}
          {flowItems.map((item) => {
            // Calculate dynamic positioning for insertion animation
            let dynamicStyle = {
              left: item.x,
              top: item.y,
              width: item.width,
              height: item.height,
            };

            // If we're showing an insertion preview, adjust positions
            if (dragOverPosition && item.type === 'panel') {
              if (item.id === dragOverPosition.leftPanelId) {
                // Move left panel slightly left
                dynamicStyle.left = item.x - 30;
              } else if (item.id === dragOverPosition.rightPanelId) {
                // Move right panel slightly right
                dynamicStyle.left = item.x + 30;
              }
            }

            // Add visual feedback for the panel being dragged
            if (draggedPanel && item.id === draggedPanel.id) {
              dynamicStyle.opacity = 0.5;
              dynamicStyle.transform = 'scale(0.95)';
            }

            return item.type === "plus" ? (
              <div
                key={item.id}
                className={`plus-sign ${item.isInsertionPoint ? 'insertion-point' : ''}`}
                style={{
                  left: item.x,
                  top: item.y,
                  ...(item.style || {})
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, item)}
              >
                +
              </div>
            ) : (
              <div
                key={item.id}
                className="flow-panel"
                style={dynamicStyle}
                draggable
                onDragStart={() => handlePanelDragStart(item)}
                onDragEnd={handlePanelDragEnd}
              >
                <div className="panel-header">
                  <span className="panel-icon">{item.icon}</span>
                  <span className="panel-title">{item.label}</span>
                </div>
                <div className="panel-image-placeholder"></div>
                <div className="panel-content">
                  {item.dummyData && (
                    <div className="panel-data">
                      {Object.entries(item.dummyData).map(([key, value], index) => (
                        <div key={key} className="data-row">
                          <span className="data-label">{key}:</span>
                          <span className="data-value">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Render temporary insertion plus sign when dragging between panels */}
          {dragOverPosition && (
            <div
              className="plus-sign insertion-preview"
              style={{
                left: dragOverPosition.x,
                top: dragOverPosition.y,
              }}
            >
              +
            </div>
          )}
        </div>
      </div>

      {/* Right Controls */}
      <div className="timeline-editor-col controls">
        <h3>Settings</h3>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Zoom: {Math.round(zoom * 100)}%
          </label>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>
        <button
          onClick={() => {
            setPanOffset({ x: -CONTAINER_WIDTH / 4, y: -CONTAINER_HEIGHT / 4 });
            setZoom(1);
          }}
          style={{
            padding: '0.5rem 1rem',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Reset View
        </button>
        {/* Placeholder for future controls */}
      </div>
    </div>
  );
}

export default TimelineEditor;